"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string };

type HummFinanceBlok = SbBlokData & {
  title?: string;
  description?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  image?: Asset;
  badge_text?: string;
  bg_color?: unknown;
  reverse_order?: boolean;
};

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function HummFinance({ blok }: { blok: HummFinanceBlok }) {
  const region = regionFromPathname(usePathname());
  const link = resolveLink(blok.button_link, region);
  const buttonLabel = blok.button_label || "Visit Humm";
  const badgeText = blok.badge_text || "How much will it cost?";
  const { bg } = resolveBg(blok.bg_color, "dusk-01");
  const imageOrder = blok.reverse_order ? "md:order-1" : "md:order-2";
  const textOrder = blok.reverse_order ? "md:order-2" : "md:order-1";

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[52px] pb-10 xl:py-[54px]">
        <div className="flex flex-col gap-[38px] md:flex-row md:items-center md:gap-12 xl:justify-end xl:gap-[156px]">
          {blok.image?.filename && (
            <div
              className={`relative order-first w-full md:flex-1 md:shrink-0 xl:h-[338px] xl:w-[492px] xl:flex-initial ${imageOrder}`}
            >
              {/* Image wrapper sized exactly to the image so the badges
                  anchor to its edges. */}
              <div className="relative mx-auto w-full max-w-[440px] xl:absolute xl:top-0 xl:right-[54px] xl:h-[338px] xl:w-[386px] xl:max-w-none">
                <div className="relative aspect-321/281 overflow-hidden rounded-[18px] xl:aspect-auto xl:h-full xl:rounded-[24px]">
                  <Image
                    src={blok.image.filename}
                    alt={blok.image.alt || ""}
                    fill
                    sizes="(min-width: 1280px) 386px, (min-width: 768px) 50vw, 100vw"
                    className="object-cover"
                  />
                </div>

                {badgeText && (
                  <div className="text-purple-05 xl:text-body absolute -top-3 right-[7px] max-w-[120px] rounded-[7px] bg-[#e0bff0] px-3 py-2 text-center text-[15px] leading-tight font-bold shadow-sm xl:top-[47px] xl:right-[-47px] xl:w-[130px] xl:max-w-none xl:rounded-[8px] xl:px-3.5 xl:py-2.5">
                    {badgeText}
                  </div>
                )}

                {/* Humm logo on orange box.
                    Mobile/tablet: anchored bottom-left of image, hangs
                    slightly below.
                    xl+: pokes out past the image's left edge, vertically
                    near middle-bottom — matches Figma (frame left=0,
                    top=184; image sits flush-right within frame so this
                    is wrapper x=-52px). */}
                <div className="absolute -bottom-3 left-[13px] flex h-[37px] w-[123px] items-center justify-center rounded-[5px] bg-[#ff5a00] xl:top-[184px] xl:bottom-auto xl:left-[-52px] xl:h-[50px] xl:w-[186px] xl:rounded-[8px]">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/assets/logo/humm-logo-w.svg"
                    alt="humm"
                    className="h-[22px] w-auto xl:h-[30px]"
                  />
                </div>
              </div>
            </div>
          )}

          <div
            className={`flex flex-col gap-8 md:flex-1 md:shrink-0 xl:w-[510px] xl:flex-initial xl:py-2.5 ${textOrder}`}
          >
            <div className="text-purple-05 flex flex-col gap-3 lg:gap-5">
              {blok.title && (
                <h2 className="text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold">
                  {blok.title}
                </h2>
              )}
              {blok.description && (
                <p className="text-body leading-[1.56] text-[#595959]">{blok.description}</p>
              )}
            </div>
            {link.href !== "#" && (
              <a
                href={link.href}
                {...(link.target ? { target: link.target } : {})}
                rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                className="text-body text-purple-04 inline-flex w-fit items-center gap-[7px] py-2.5 leading-[1.054] font-extrabold hover:underline"
              >
                {buttonLabel}
                <ArrowIcon />
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
